import React, { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { fetchCompetitionsData } from "../../action/competitionsActions";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import CustomDeleteModal from "../../component/Modals/CustomDeleteModal";
import ConfirmModel from "../../component/Modals/ConfirmModel";
import BannerSkeleton from "../../component/skeleton/BannerSkeleton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import Pagination from "../../component/Pagination";
import { BEAUTY_PAGEANT_LABEL } from "../../utils/homeFlow";
import { formatCompetitionDateTime } from "../../utils/competitionDateTime";

const columns = [
  { id: "banner", label: "BANNER", width: "14%" },
  { id: "name", label: "NAME", width: "18%" },
  { id: "categories", label: "CATEGORIES", width: "18%" },
  { id: "dates", label: "DATES", width: "16%" },
  { id: "voting", label: "VOTING %", width: "10%" },
  { id: "status", label: "STATUS", width: "10%" },
  { id: "actions", label: "ACTIONS", width: "24%" },
];

const statusColor = {
  scheduled: "default",
  running: "success",
  paused: "warning",
  stopped: "error",
};

const statusLabel = {
  scheduled: "SCHEDULED",
  running: "ACTIVE",
  paused: "PAUSED",
  stopped: "STOPPED",
};

const DATE_FMT = "DD MMM YYYY, HH:mm";

const ACTION_SLOT_SX = {
  width: 34,
  height: 34,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

function ActionSlot({ children }) {
  return <Box sx={ACTION_SLOT_SX}>{children}</Box>;
}

function DisabledActionButton({ title, sx, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled
          sx={{
            border: "1px solid #eceef3",
            borderRadius: 1.5,
            color: "#9ca3af",
            opacity: 0.45,
            ...sx,
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

const Competitions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const competitionsData = useSelector((state) => state.competitions);
  const token = useSelector((state) => state.auth.token);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const currentRef = useRef(null);
  const currentCallback = useRef(null);
  const currentTextRef = useRef(null);
  const [stopConfirm, setStopConfirm] = useState({
    isOpen: false,
    id: null,
  });

  useEffect(() => {
    dispatch(fetchCompetitionsData(token, 1));
  }, [dispatch, token]);

  useEffect(() => {
    const msg = location.state?.toastMessage;
    if (msg) {
      if (location.state?.toastType === "error") toast.error(msg);
      else toast.success(msg);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (competitionsData?.data?.success) {
      const list = competitionsData.data?.data?.rows || [];
      if (list.length > 0) {
        setRows(list);
        setCurrentPage(competitionsData.data.data.currentPage || 1);
        setTotalPages(Math.ceil((competitionsData.data.data.count || 0) / 10));
      } else {
        setRows("NO Data");
      }
    } else if (competitionsData?.data && !competitionsData.data.success) {
      setRows(competitionsData.data.message);
    }
  }, [competitionsData]);

  const handleChangePage = (_, newPage) => {
    dispatch(fetchCompetitionsData(token, newPage));
  };

  const handleDelete = async (ref) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteCompetition/${ref.current.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Competition deleted successfully");
        dispatch(fetchCompetitionsData(token, currentPage));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Please try again..");
    } finally {
      setOpenDelete(false);
    }
  };

  const handleStatus = async (id, action) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateCompetitionStatus`,
        { id, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        dispatch(fetchCompetitionsData(token, currentPage));
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Status update failed");
    }
  };

  const actionBtnSx = {
    border: "1px solid #e5e7eb",
    borderRadius: 1.5,
    color: "#374151",
    "&:hover": { background: "#f3f4f6", borderColor: "#d1d5db" },
  };

  return (
    <>
      <ToastContainer />
      <Box className="page_title custom-header">
        <Box>
          <Typography variant="h1" component="h1">
            Competitions
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 0.5 }}>
            Manage {BEAUTY_PAGEANT_LABEL} competitions — setup in Add/Edit, live Final Night
            control from the ✨ icon after Stop
          </Typography>
        </Box>
        <Button
          className="create-custom"
          onClick={() => navigate("/admin/dashboard/competitions/add")}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          <AddIcon className="delete-icon" /> Create Competition
        </Button>
      </Box>
      <Box className="page_inner">
        <Box className="page_table tournament">
          <TableContainer
            component={Paper}
            className="customer_form user_table"
            sx={{
              borderRadius: 2,
              overflowX: "auto",
              overflowY: "hidden",
              boxShadow: "none",
              border: "1px solid #eceef3",
            }}
          >
            <Table style={{ width: "100%", minWidth: 1100 }}>
              <TableHead className="table_Head">
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sx={{ width: column.width }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(rows) && rows.length > 0 ? (
                  rows.map((value) => (
                    <TableRow key={value.id} hover>
                      <TableCell>
                        <Box
                          className="bannerImageOuter"
                          sx={{
                            width: 88,
                            height: 56,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            "& img": {
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            },
                          }}
                        >
                          <img src={value.banner_img} alt="banner" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography className="table-text" fontWeight={600}>
                          {value.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {(value.categories || []).map((c) => (
                            <Chip
                              key={c.id || c.category}
                              size="small"
                              label={c.category}
                              sx={{ background: "#fff5f6", color: "#ef364e" }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {formatCompetitionDateTime(value.start_date, DATE_FMT)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          to {formatCompetitionDateTime(value.end_date, DATE_FMT)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`J ${value.judges_percent}% / U ${value.users_percent}%`}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack gap={0.5}>
                          <Chip
                            size="small"
                            label={
                              statusLabel[value.status] ||
                              (value.status || "").toUpperCase()
                            }
                            color={statusColor[value.status] || "default"}
                          />
                          {value.final_night_status &&
                          value.final_night_status !== "off" ? (
                            <Chip
                              size="small"
                              icon={
                                <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
                              }
                              label={
                                value.final_night_status === "live"
                                  ? "FN LIVE"
                                  : "FN ENDED"
                              }
                              sx={{
                                bgcolor:
                                  value.final_night_status === "live"
                                    ? "rgba(201,166,107,0.2)"
                                    : "#f3f4f6",
                                color:
                                  value.final_night_status === "live"
                                    ? "#9a7b3f"
                                    : "#6b7280",
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            />
                          ) : null}
                          {value.status === "stopped" ? (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#b91c1c",
                                lineHeight: 1.3,
                                maxWidth: 120,
                              }}
                            >
                              Permanently stopped — cannot resume
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          gap={0.5}
                          alignItems="center"
                          flexWrap="nowrap"
                          sx={{ minWidth: 280 }}
                        >
                          <ActionSlot>
                            {value.status === "running" ? (
                              <Tooltip title="Pause competition">
                                <IconButton
                                  size="small"
                                  sx={actionBtnSx}
                                  onClick={() =>
                                    handleStatus(value.id, "pause")
                                  }
                                >
                                  <PauseCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : value.status === "paused" ? (
                              <Tooltip title="Resume competition">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionBtnSx, color: "#2e7d32" }}
                                  onClick={() =>
                                    handleStatus(value.id, "resume")
                                  }
                                >
                                  <PlayCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : value.status === "stopped" ? (
                              <DisabledActionButton title="Stopped competitions cannot be resumed">
                                <PlayCircleOutlineIcon fontSize="small" />
                              </DisabledActionButton>
                            ) : (
                              <DisabledActionButton title="Competition has not started yet">
                                <PauseCircleOutlineIcon fontSize="small" />
                              </DisabledActionButton>
                            )}
                          </ActionSlot>

                          <ActionSlot>
                            {value.status !== "stopped" ? (
                              <Tooltip title="Stop permanently — cannot be resumed">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionBtnSx, color: "#d32f2f" }}
                                  onClick={() =>
                                    setStopConfirm({
                                      isOpen: true,
                                      id: value.id,
                                    })
                                  }
                                >
                                  <StopCircleOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <DisabledActionButton title="Already stopped permanently">
                                <StopCircleOutlinedIcon fontSize="small" />
                              </DisabledActionButton>
                            )}
                          </ActionSlot>

                          <ActionSlot>
                            <Tooltip title="Register contestant & upload video or image">
                              <IconButton
                                size="small"
                                sx={{ ...actionBtnSx, color: "#7c3aed" }}
                                onClick={() =>
                                  navigate(
                                    `/admin/dashboard/competitions/${value.id}/register`
                                  )
                                }
                              >
                                <PersonAddAlt1Icon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionSlot>

                          <ActionSlot>
                            {value.status !== "stopped" ||
                            value.final_night_status === "live" ||
                            value.final_night_status === "ended" ? (
                              <Tooltip
                                title={
                                  value.final_night_status === "live" ||
                                  value.final_night_status === "ended"
                                    ? "Add / deduct contestant votes (Final Night)"
                                    : "Add / deduct contestant votes (pre-round)"
                                }
                              >
                                <IconButton
                                  size="small"
                                  sx={{ ...actionBtnSx, color: "#059669" }}
                                  onClick={() =>
                                    navigate(
                                      `/admin/dashboard/competitions/${value.id}/vote-adjust`
                                    )
                                  }
                                >
                                  <HowToVoteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <DisabledActionButton title="Vote adjustment is unavailable after the competition is stopped">
                                <HowToVoteIcon fontSize="small" />
                              </DisabledActionButton>
                            )}
                          </ActionSlot>

                          <ActionSlot>
                            <Tooltip title="Edit competition">
                              <IconButton
                                size="small"
                                sx={{ ...actionBtnSx, color: "#1976d2" }}
                                onClick={() =>
                                  navigate(
                                    `/admin/dashboard/competitions/edit/${value.id}`
                                  )
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionSlot>

                          <ActionSlot>
                            {value.status === "stopped" ||
                            value.final_night_status === "live" ||
                            value.final_night_status === "ended" ? (
                              <Tooltip title="Final Night live control — stream, enable rounds, scores">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionBtnSx, color: "#9a7b3f" }}
                                  onClick={() =>
                                    navigate(
                                      `/admin/dashboard/competitions/final-night/${value.id}`
                                    )
                                  }
                                >
                                  <AutoAwesomeOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <DisabledActionButton title="Final Night control is available after the competition is stopped">
                                <AutoAwesomeOutlinedIcon fontSize="small" />
                              </DisabledActionButton>
                            )}
                          </ActionSlot>

                          <ActionSlot>
                            <Tooltip title="Delete competition">
                              <IconButton
                                size="small"
                                sx={{ ...actionBtnSx, color: "#6b7280" }}
                                onClick={() => {
                                  currentRef.current = value;
                                  currentCallback.current = handleDelete;
                                  currentTextRef.current =
                                    "Are you sure you want to delete this competition?";
                                  setOpenDelete(true);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionSlot>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : rows !== "NO Data" ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <BannerSkeleton key={i} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 6, color: "#6b7280" }}
                    >
                      No competitions yet. Create your first one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handleChangePage={handleChangePage}
            />
          )}
        </Box>
      </Box>
      <CustomDeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        selected={currentRef}
        deleteCallBack={currentCallback}
        customText={currentTextRef.current}
      />
      <ConfirmModel
        isOpen={stopConfirm.isOpen}
        title="Stop competition permanently?"
        description="Once stopped, this competition can never be started or resumed again. This action cannot be undone."
        confirmText="Stop permanently"
        cancelText="Cancel"
        onClose={() => setStopConfirm({ isOpen: false, id: null })}
        onCancel={() => setStopConfirm({ isOpen: false, id: null })}
        onConfirm={async () => {
          const id = stopConfirm.id;
          setStopConfirm({ isOpen: false, id: null });
          if (id) await handleStatus(id, "stop");
        }}
      />
    </>
  );
};

export default Competitions;
